/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  useToast,
  VStack,
  Text,
  Spinner,
  Center,
  HStack,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Flex,
  Tabs,
  TabList,
  Tab,
  SimpleGrid,
} from '@chakra-ui/react';
import { useMonthlyReportBooks } from '@partiu/api/dashboard/hooks.ts';
import { axiosInstance } from '@partiu/api/axios';
import { BookCard, EmptyState } from '../components';
import { isMagazineType } from '../utils.ts';

// Função para formatar números com separadores de milhares
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('pt-BR').format(num);
};

function StatBox({ label, value, suffix, scheme }: { label: string; value: number; suffix: string; scheme: string }) {
  return (
    <Box
      textAlign="center"
      px={2}
      py={3}
      borderRadius="md"
      bg={`${scheme}.50`}
      border="1px solid"
      borderColor={`${scheme}.100`}
      minW={0}
      overflow="hidden"
    >
      <Text
        fontSize="xs"
        color={`${scheme}.600`}
        fontWeight="semibold"
        mb={2}
        textTransform="uppercase"
        letterSpacing="wide"
        noOfLines={1}
      >
        {label}
      </Text>
      <Text
        color={`${scheme}.600`}
        fontSize="md"
        fontWeight="bold"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        lineHeight="1.2"
        mb={2}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatNumber(value)}
      </Text>
      <Text fontSize="xs" color={`${scheme}.500`} noOfLines={1}>
        {suffix}
      </Text>
    </Box>
  );
}

const REFRESH_STATS_URL = '/agreement_staff/refresh_stats/';

/** Dispara o recálculo das estatísticas (Celery). Back limita a 1 disparo / 15 min por convênio. */
function RefreshStatsButton() {
  const toast = useToast();
  const [retryAfter, setRetryAfter] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axiosInstance
      .get(REFRESH_STATS_URL)
      .then(({ data }) => setRetryAfter(data.can_refresh ? 0 : data.retry_after))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const id = setTimeout(() => setRetryAfter((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [retryAfter]);

  const handleClick = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.post(REFRESH_STATS_URL);
      setRetryAfter(data.retry_after);
      toast({ description: data.detail, status: 'success', isClosable: true });
    } catch (err: any) {
      const data = err?.response?.data;
      if (err?.response?.status === 429) setRetryAfter(data.retry_after);
      toast({ description: data?.detail || 'Erro ao atualizar estatísticas.', status: 'warning', isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const minutes = Math.floor(retryAfter / 60);
  const seconds = String(retryAfter % 60).padStart(2, '0');

  return (
    <Button size="sm" colorScheme="blue" variant="outline" onClick={handleClick} isLoading={loading} isDisabled={retryAfter > 0}>
      {retryAfter > 0 ? `Aguarde ${minutes}:${seconds}` : '🔄 Atualizar estatísticas'}
    </Button>
  );
}

export function BooksGroupedTab() {
  const [selectedType, setSelectedType] = useState<string>('all');

  // Usar o novo hook para buscar dados do relatório mensal
  const { data: monthlyReportData, isLoading, error } = useMonthlyReportBooks();
  const groupedData = useMemo(() => {
    if (!monthlyReportData?.data) return {};

    const grouped: { [monthYear: string]: any[] } = {};

    monthlyReportData.data.forEach((monthData) => {
      const monthYearLabel = monthData.label;

      const books = monthData.books.map((reportBook) => ({
        ...reportBook,
        receivedAt: reportBook.createdAt,
        document: reportBook.document,
        descriptionReceived: reportBook.description,
        exchanged: reportBook.exchanged,
        exchangedDate: reportBook.exchangedDate,
        hasSentEmailNotification: reportBook.hasSentEmailNotification,
        sentEmailAt: reportBook.sentEmailAt,
        agreement: reportBook.agreement,
        exchangedBook: reportBook.exchangedBook,
      }));
      grouped[monthYearLabel] = books;
    });

    return grouped;
  }, [monthlyReportData]);

  // Calcular totais por tipo baseado nos dados agrupados
  const totalByType = useMemo(() => {
    const totals = { ebooks: 0, magazines: 0, audiobooks: 0 };

    Object.values(groupedData).forEach((books) => {
      books.forEach((book: any) => {
        if (book.type === 'book') {
          totals.ebooks++;
        } else if (isMagazineType(book.type)) {
          totals.magazines++;
        } else if (book.type === 'audiobook') {
          totals.audiobooks++;
        }
      });
    });

    return totals;
  }, [groupedData]);

  /** Contador da aba "Todos": soma os mesmos títulos que as abas de tipo filtram,
   * para os quatro números fecharem entre si (o card "Total" acima continua vindo
   * de `statistics.totalMedia`). */
  const totalBooks = totalByType.ebooks + totalByType.magazines + totalByType.audiobooks;

  // Filtrar livros por tipo selecionado
  const filteredGroupedData = useMemo(() => {
    if (selectedType === 'all') return groupedData;

    const filtered: { [monthYear: string]: any[] } = {};
    Object.entries(groupedData).forEach(([monthYear, books]) => {
      const filteredBooks = books.filter((book: any) =>
        selectedType === 'magazine' ? isMagazineType(book.type) : book.type === selectedType
      );
      if (filteredBooks.length > 0) {
        filtered[monthYear] = filteredBooks;
      }
    });

    return filtered;
  }, [groupedData, selectedType]);

  // Removido: configuração de envio movida para a nova aba dedicada

  if (isLoading) {
    return (
      <Center p={8}>
        <Spinner size="lg" color="blue.500" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center p={8}>
        <VStack spacing={4}>
          <Text color="red.500" fontSize="lg" fontWeight="bold">
            Erro ao carregar relatório mensal
          </Text>
          <Text color="gray.500" textAlign="center">
            {error instanceof Error ? error.message : 'Ocorreu um erro inesperado. Tente novamente mais tarde.'}
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box p={4}>
      <VStack spacing={4} align="stretch">
        {/* Resumo geral moderno */}
        <Card
          bg="white"
          border="none"
          shadow="lg"
          borderRadius="xl"
          overflow="hidden"
          position="relative"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            bgGradient: 'linear(to-r, blue.400, purple.400, pink.400)',
          }}
        >
          <CardBody p={{ base: 4, md: 6 }}>
            <VStack spacing={4}>
              <Box textAlign="center">
                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color="gray.800" mb={1}>
                  📊 Relatório Geral de Títulos
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Visão consolidada dos seus títulos digitais
                </Text>
              </Box>

              <RefreshStatsButton />

              <SimpleGrid minChildWidth="150px" spacing={3} w="100%">
                <StatBox
                  label="Total"
                  value={monthlyReportData?.statistics?.totalMedia || 0}
                  suffix="títulos"
                  scheme="blue"
                />
                <StatBox
                  label="Período"
                  value={monthlyReportData?.statistics?.totalPeriods || 0}
                  suffix="meses"
                  scheme="green"
                />
                <StatBox
                  label="Entregues"
                  value={monthlyReportData?.statistics?.totalDelivered || 0}
                  suffix="total"
                  scheme="purple"
                />
                <StatBox
                  label="📚 Ebooks"
                  value={monthlyReportData?.statistics?.totalEbooks || 0}
                  suffix="entregues"
                  scheme="red"
                />
                <StatBox
                  label="🎧 Audiobooks"
                  value={monthlyReportData?.statistics?.totalAudiobooks || 0}
                  suffix="entregues"
                  scheme="teal"
                />
                <StatBox
                  label="📰 Revistas"
                  value={monthlyReportData?.statistics?.totalMagazines || 0}
                  suffix="entregues"
                  scheme="yellow"
                />
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>

        {/* Seção de configuração de envio movida para a nova aba "Configuração de Envio" */}

        {/* Filtros por tipo */}
        <Card shadow="sm" border="1px solid" borderColor="gray.100">
          <CardHeader pb={2}>
            <Text fontSize="md" fontWeight="bold" color="gray.700">
              Filtrar por Tipo de Título
            </Text>
          </CardHeader>
          <CardBody pt={0}>
            <Tabs
              index={selectedType === 'all' ? 0 : selectedType === 'book' ? 1 : selectedType === 'magazine' ? 2 : 3}
              onChange={(index) => {
                const types = ['all', 'book', 'magazine', 'audiobook'];
                setSelectedType(types[index]);
              }}
              variant="enclosed"
              colorScheme="blue"
            >
              <TabList>
                <Tab>Todos ({formatNumber(totalBooks)})</Tab>
                <Tab>📚 Ebooks ({formatNumber(totalByType.ebooks)})</Tab>
                <Tab>📰 Revistas ({formatNumber(totalByType.magazines)})</Tab>
                <Tab>🎧 Audiobooks ({formatNumber(totalByType.audiobooks)})</Tab>
              </TabList>
            </Tabs>
          </CardBody>
        </Card>

        {/* Dados agrupados por mês */}
        {Object.keys(filteredGroupedData).length > 0 ? (
          <VStack spacing={4} align="stretch">
            {Object.entries(filteredGroupedData).map(([monthYear, books]) => (
              <Card key={monthYear} shadow="sm" border="1px solid" borderColor="gray.100">
                <CardHeader pb={2}>
                  <Flex justify="space-between" align="center">
                    <Text fontSize="lg" fontWeight="bold" color="gray.700">
                      {monthYear}
                    </Text>
                    <Badge colorScheme="blue" variant="subtle" px={2} py={1}>
                      {formatNumber(books.length)} {books.length === 1 ? 'título' : 'títulos'}
                    </Badge>
                  </Flex>
                </CardHeader>
                <CardBody pt={0}>
                  <Box
                    overflowX="auto"
                    pb={4}
                    css={{
                      '&::-webkit-scrollbar': {
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#c1c1c1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#a8a8a8',
                      },
                    }}
                  >
                    <HStack spacing={3} align="stretch" minW="max-content" pb={2}>
                      {books.map((book: any) => (
                        <Box key={book.id} flexShrink={0}>
                          <BookCard
                            book={book.book || book}
                            size="sm"
                            showAuthor={true}
                            showPages={false}
                            showDate={true}
                            showInfo={true}
                            date={book.createdAt}
                            description={book.description}
                          />
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                </CardBody>
              </Card>
            ))}
          </VStack>
        ) : (
          <EmptyState
            type="books"
            title={
              selectedType === 'all'
                ? 'Nenhum título encontrado'
                : `Nenhum ${selectedType === 'book' ? 'livro' : selectedType === 'magazine' ? 'revista' : 'audiobook'} encontrado`
            }
            actionText="Verifique se há dados disponíveis"
          />
        )}
      </VStack>
    </Box>
  );
}
